'use client';

import {
  Bot,
  ExternalLink,
  HardDrive,
  type LucideIcon,
  Mailbox,
  Plug,
  Video,
  Zap,
} from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { cn } from './cn';

/**
 * The Crowlingo applications (docs/adr/0004-service-roles.md; the mockup, brand/crow-ecosysteme.html):
 * one product, several apps. Their colour square says "a Crowlingo application" everywhere; their
 * addresses are the deployment's (`GET /api/ecosystem`, `@crow/service-kit/ecosystem`).
 */

export type CrowAppId = 'crowdrive' | 'crowbox' | 'crowroom' | 'crowdex' | 'crowlink' | 'crowpus';

export interface CrowApp {
  id: CrowAppId;
  name: string;
  /** What it is, in two words. */
  description: string;
  icon: LucideIcon;
}

/** The applications, in the switcher's order. */
export const CROW_APPS: readonly CrowApp[] = [
  { id: 'crowdrive', name: 'CrowDrive', description: 'Fichiers', icon: HardDrive },
  { id: 'crowbox', name: 'CrowBox', description: 'Mail et agenda', icon: Mailbox },
  { id: 'crowroom', name: 'CrowRoom', description: 'Réunions', icon: Video },
  { id: 'crowdex', name: 'Crowdex', description: 'Agents', icon: Bot },
  { id: 'crowlink', name: 'CrowLink', description: 'Connexions', icon: Plug },
  { id: 'crowpus', name: 'Crowpus', description: 'Modèles', icon: Zap },
];

export function crowApp(id: string): CrowApp | undefined {
  return CROW_APPS.find((app) => app.id === id);
}

/** An application's accent, from the theme every app loads (`--crow-<app>-accent`). */
export const appAccent = (id: CrowAppId) => `var(--crow-${id}-accent)`;

/** Where the pages read the deployment's addresses. */
export const ECOSYSTEM_PATH = '/api/ecosystem';

type Addresses = ReadonlyMap<string, string>;

/** The Crowlingo account's pages in Zitadel (`ecosystemAccount` of `@crow/service-kit/ecosystem`). */
export interface EcosystemAccount {
  url: string;
  organizationUrl: string | null;
}

interface Ecosystem {
  addresses: Addresses;
  account: EcosystemAccount | null;
}

let ecosystem: Promise<Ecosystem> | null = null;
const EMPTY: Addresses = new Map();

function readEcosystem(fetchImpl: typeof fetch = fetch): Promise<Ecosystem> {
  ecosystem ??= fetchImpl(ECOSYSTEM_PATH, { credentials: 'same-origin' })
    .then((response) => (response.ok ? response.json() : {}))
    .then((body: { apps?: { id: string; url: string }[]; account?: EcosystemAccount | null }) => ({
      addresses: new Map((body.apps ?? []).map((app) => [app.id, app.url])),
      account: body.account ?? null,
    }))
    .catch(() => ({ addresses: new Map(), account: null }));
  return ecosystem;
}

/** The deployment's addresses of the apps, read once per page (again after `resetEcosystem`). */
export function loadEcosystem(fetchImpl: typeof fetch = fetch): Promise<Addresses> {
  return readEcosystem(fetchImpl).then((read) => read.addresses);
}

const readers = new Set<() => void>();

/**
 * Forgets the addresses read, and has the mounted hooks read them again: when what the server
 * names changes during the session (Crowdex on a computer, its user linking or unlinking the cloud).
 */
export function resetEcosystem(): void {
  ecosystem = null;
  for (const reread of readers) reread();
}

function useRead<T>(pick: (read: Ecosystem) => T, initial: T): T {
  const [value, setValue] = useState<T>(initial);
  // biome-ignore lint/correctness/useExhaustiveDependencies: read once per mount, and on each reset
  useEffect(() => {
    let latest = 0;
    const read = () => {
      const asked = ++latest;
      void readEcosystem().then((answer) => asked === latest && setValue(pick(answer)));
    };
    read();
    readers.add(read);
    return () => {
      latest = -1;
      readers.delete(read);
    };
  }, []);
  return value;
}

/** The apps' addresses (empty until read, and where the deployment names none). */
export function useEcosystem(): Addresses {
  return useRead<Addresses>((read) => read.addresses, EMPTY);
}

/** The account's pages (null until read, or where the deployment has no issuer). */
export function useEcosystemAccount(): EcosystemAccount | null {
  return useRead((read) => read.account, null);
}

/** What the visitor opened last in another app (`RECENT_PATH` of `@crow/service-kit/ecosystem`). */
export interface RecentItem {
  app: CrowAppId;
  title: string;
  url: string;
  at: string;
  kind?: string;
}

export const RECENT_PATH = '/api/ecosystem/recent';

/**
 * The other apps' recent items, newest first: each app is asked with the visitor's session (the
 * apps share a site); an app that does not answer is left out.
 */
export async function loadRecents(
  addresses: Addresses,
  current: CrowAppId,
  fetchImpl: typeof fetch = fetch,
): Promise<RecentItem[]> {
  const lists = await Promise.all(
    CROW_APPS.filter((app) => app.id !== current && addresses.has(app.id)).map(async (app) => {
      try {
        const response = await fetchImpl(new URL(RECENT_PATH, addresses.get(app.id)).toString(), {
          credentials: 'include',
        });
        if (!response.ok) return [];
        const body = (await response.json()) as { items?: Omit<RecentItem, 'app'>[] };
        return (body.items ?? []).map((item) => ({ ...item, app: app.id }));
      } catch {
        return [];
      }
    }),
  );
  return lists.flat().sort((a, b) => b.at.localeCompare(a.at));
}

/** The other apps' recent items, read when `enabled` turns true (the switcher opens). */
export function useRecents(current: CrowAppId, enabled: boolean): RecentItem[] | null {
  const addresses = useEcosystem();
  const [items, setItems] = useState<RecentItem[] | null>(null);
  useEffect(() => {
    if (!enabled || addresses.size === 0) return;
    let live = true;
    void loadRecents(addresses, current).then((read) => live && setItems(read));
    return () => {
      live = false;
    };
  }, [enabled, addresses, current]);
  return items;
}

export const SEARCH_PATH = '/api/ecosystem/search';

/** One app's items matching words (`SEARCH_PATH`), with the visitor's session; empty when it cannot say. */
export async function searchApp(
  addresses: Addresses,
  app: CrowAppId,
  q: string,
  fetchImpl: typeof fetch = fetch,
): Promise<RecentItem[]> {
  const origin = addresses.get(app);
  if (!origin || !q.trim()) return [];
  try {
    const url = new URL(SEARCH_PATH, origin);
    url.searchParams.set('q', q);
    const response = await fetchImpl(url.toString(), { credentials: 'include' });
    if (!response.ok) return [];
    const body = (await response.json()) as { items?: Omit<RecentItem, 'app'>[] };
    return (body.items ?? []).map((item) => ({ ...item, app }));
  } catch {
    return [];
  }
}

export const SIGNOUT_PATH = '/api/ecosystem/signout';
export const ECOSYSTEM_HEADER = 'x-crow-ecosystem';

/**
 * Ends the visitor's session in every other app (`SIGNOUT_PATH`), before the current app ends its
 * own: one sign-in, one sign-out. An app that does not answer is not waited for long.
 */
export async function signOutEverywhere(
  current: CrowAppId,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const addresses = await loadEcosystem(fetchImpl);
  await Promise.all(
    [...addresses]
      .filter(([id]) => id !== current)
      .map(([, url]) =>
        fetchImpl(new URL(SIGNOUT_PATH, url).toString(), {
          method: 'POST',
          credentials: 'include',
          headers: { [ECOSYSTEM_HEADER]: '1' },
          signal: AbortSignal.timeout(3000),
        }).catch(() => undefined),
      ),
  );
}

/** A link to a page of another app (`appUrl('crowdrive', '/files/1')`); null without its address. */
export function appUrl(ecosystem: Addresses, id: CrowAppId, path = '/'): string | null {
  const origin = ecosystem.get(id);
  return origin ? new URL(path, origin).toString() : null;
}

/**
 * The query parameter a host (Crowdex) adds when it shows an app in a tab of its own; its server
 * adds it with `embeddedUrl` of `@crow/service-kit/ecosystem`, under the same name.
 */
export const EMBEDDED_PARAM = 'crow_embedded';
const EMBEDDED_KEY = 'crow:embedded';

/**
 * Whether this page is shown inside another Crowlingo app (a Crowdex tab): then it hides its own
 * top bar (no switcher nor account menu within the host's). Told by `?crow_embedded=` once, kept for
 * the tab, or by being in a frame.
 */
export function isEmbedded(
  win: Window | undefined = typeof window === 'undefined' ? undefined : window,
): boolean {
  if (win === undefined) return false;
  try {
    if (new URL(win.location.href).searchParams.has(EMBEDDED_PARAM)) {
      win.sessionStorage.setItem(EMBEDDED_KEY, '1');
      return true;
    }
    if (win.sessionStorage.getItem(EMBEDDED_KEY) === '1') return true;
  } catch {
    // Storage refused: the frame test still says.
  }
  return win.self !== win.top;
}

/** An app's page as a host shows it in a tab: told it is embedded. */
export function embeddedUrl(url: string, host: CrowAppId = 'crowdex'): string {
  const parsed = new URL(url);
  parsed.searchParams.set(EMBEDDED_PARAM, host);
  return parsed.toString();
}

/** An application's mark: its colour square and shape. */
export function AppMark({
  app,
  size = 28,
  className,
}: {
  app: CrowAppId;
  size?: number;
  className?: string;
}) {
  const known = crowApp(app);
  const Icon = known?.icon ?? Bot;
  return (
    <span
      aria-hidden
      className={cn('inline-grid shrink-0 place-items-center rounded-[28%] text-white', className)}
      style={{ width: size, height: size, background: appAccent(app) }}
    >
      <Icon style={{ width: size * 0.55, height: size * 0.55 }} strokeWidth={1.75} />
    </span>
  );
}

/** The origin dot of something that comes from another application. */
export function AppDot({ app, className }: { app: CrowAppId; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('inline-block size-2 shrink-0 rounded-full', className)}
      style={{ background: appAccent(app) }}
    />
  );
}

/** "Crowlingo", with the origin dot: a plugin or an item an application brings. */
export function CrowlingoBadge({ app, className }: { app: CrowAppId; className?: string }) {
  const known = crowApp(app);
  return (
    <span
      title={known ? `Fourni par ${known.name}` : undefined}
      className={cn(
        'inline-flex h-[18px] shrink-0 items-center gap-1.5 rounded-full border border-crow-line px-[7px] text-crow-label font-semibold text-crow-text-2',
        className,
      )}
    >
      <AppDot app={app} className="size-1.5" />
      Crowlingo
    </span>
  );
}

/** "fournis par CrowDrive": what a space or a list owes to another application. */
export function ProvidedBy({
  app,
  children,
  className,
}: {
  app: CrowAppId;
  children?: ReactNode;
  className?: string;
}) {
  const known = crowApp(app);
  return (
    <span
      className={cn(
        'inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full border border-crow-line px-2.5 text-crow-label font-semibold whitespace-nowrap text-crow-text-2',
        className,
      )}
    >
      <AppDot app={app} />
      {children ?? `fournis par ${known?.name ?? app}`}
    </span>
  );
}

/**
 * Something that lives in another application (a CrowDrive file in a meeting, a CrowBox thread):
 * its icon, title and detail, and a link to it in its app, with the app's origin dot.
 */
export function ReferenceCard({
  app,
  icon,
  title,
  meta,
  href,
  action = 'Ouvrir',
  className,
}: {
  app: CrowAppId;
  icon?: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  /** Where it opens; without it the card shows no link. */
  href?: string | null;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 items-center gap-3 rounded-crow-control border border-crow-line bg-crow-surface px-3 py-2.5',
        className,
      )}
    >
      {icon ? (
        <span className="grid size-8 shrink-0 place-items-center rounded-md bg-crow-raised text-crow-text-2 [&_svg]:size-4">
          {icon}
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <p className="truncate text-crow-compact text-crow-text">{title}</p>
        {meta ? <p className="truncate text-crow-label text-crow-text-3">{meta}</p> : null}
      </div>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title={`Ouvrir dans ${crowApp(app)?.name ?? app}`}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-crow-line px-2 py-0.5 text-crow-label font-semibold text-crow-text-2 hover:bg-crow-raised"
        >
          <AppDot app={app} className="size-1.5" />
          {action}
          <ExternalLink className="size-3" />
        </a>
      ) : (
        <AppDot app={app} />
      )}
    </div>
  );
}
